// Окно поверх экрана — одно на весь кабинет. Нативный <dialog> + showModal():
// затемнение (`dialog.modal::backdrop`), ловушка фокуса, недоступность страницы
// под окном и Escape — без своего кода. Окно монтируется открытым и закрывается
// снятием с экрана: «открыто» живёт у экрана, а не в DOM.
// Свело воедино три одинаковых окна времён переноса (screens/modal.tsx,
// screens/teachDialog.tsx, ConfirmDialog из «Диалогов»).
import { useEffect, useId, useRef, type ReactNode } from "react";

import { Icon } from "./Icon";

export function Modal({
  title,
  onClose,
  children,
  size = "default",
  busy = false,
  closeLabel = "Закрыть",
  initialFocus,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** wide — окно с инструкцией по шагам; form — окно с формой: шире,
   *  прокручивается, на телефоне — лист снизу. */
  size?: "default" | "wide" | "form";
  /** Пока идёт запрос, Escape, крестик и щелчок мимо окно не закрывают. */
  busy?: boolean;
  closeLabel?: string;
  /** Что получает фокус первым: селектор, иначе `[data-autofocus]`,
   *  иначе первое поле или кнопка содержимого (не крестик). */
  initialFocus?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    const target =
      (initialFocus && dialog.querySelector<HTMLElement>(initialFocus)) ||
      dialog.querySelector<HTMLElement>("[data-autofocus]") ||
      dialog.querySelector<HTMLElement>(
        ".modal__body input, .modal__body select, .modal__body textarea, .modal__body button",
      );
    target?.focus();
    return () => {
      if (dialog.open) dialog.close();
    };
    // Фокус ставится один раз при открытии; смена шага внутри окна сама решает, куда его перевести.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cls = ["modal", size === "wide" && "modal--wide", size === "form" && "modal--sheet modal--form"]
    .filter(Boolean)
    .join(" ");

  return (
    <dialog
      ref={ref}
      className={cls}
      aria-labelledby={titleId}
      onCancel={(event) => {
        // Escape закрываем сами, чтобы экран знал, что окна больше нет.
        event.preventDefault();
        if (!busyRef.current) onClose();
      }}
      onMouseDown={(event) => {
        // Щелчок по затемнению приходит на сам <dialog>, но вне его рамки.
        if (event.target !== event.currentTarget || busyRef.current) return;
        const box = event.currentTarget.getBoundingClientRect();
        const inside =
          event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
        if (!inside) onClose();
      }}
    >
      <div className="modal__head">
        <h2 id={titleId}>{title}</h2>
        <button type="button" className="icon-btn" aria-label={closeLabel} disabled={busy} onClick={onClose}>
          <Icon name="x" />
        </button>
      </div>
      <div className="modal__body">{children}</div>
    </dialog>
  );
}

/** Подтверждение необратимого или заметного действия: что случится — словами,
 *  кнопка называет действие. Фокус — на «Отмене»: Enter по привычке ничего
 *  не удалит и никого не отключит. */
export function ConfirmDialog({
  title,
  text,
  children,
  confirmLabel,
  cancelLabel = "Отмена",
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  text?: string;
  children?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Опасный вариант кнопки — для действий, после которых нечего вернуть. */
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal title={title} onClose={onCancel} busy={busy} initialFocus="[data-cancel]">
      {text && <p className="muted">{text}</p>}
      {children}
      <div className="modal__actions">
        <button type="button" className="btn" data-cancel onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </button>
        <button
          type="button"
          className={danger ? "btn btn--danger" : "btn btn--primary"}
          aria-busy={busy || undefined}
          disabled={busy}
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
